/**
 * WidgetPanel — the slide-in right-edge tray that hosts widgets in the
 * "panel" surface. Same WidgetHost as the desktop grid; only the container
 * differs. Toggled from the menu-bar LayoutGrid button (osStore.togglePanel).
 *
 * Closed state renders nothing (no pointer interception, widgets unmounted).
 * "＋ Add widget" reuses contextMenuStore (flat candidate list) exactly like
 * DesktopGrid's openAddWidgetMenu — ContextMenuItem has no submenu support.
 */
import { useEffect, useState, type MouseEvent as ReactMouseEvent } from "react";
import { osStore } from "../os/store";
import { contextMenuStore, type ContextMenuItem } from "../os/context-menu";
import { WidgetHost } from "./WidgetHost";

function openAddWidgetMenu(x: number, y: number) {
  const mods = osStore.widgetModules();
  const items: ContextMenuItem[] = mods.length
    ? mods.map((m) => ({
        id: `add-panel-${m.id}`,
        label: m.widget?.title || m.dock?.label || m.name,
        icon: m.dock?.icon,
        action: () => osStore.addPanelItem(m.id),
      }))
    : [
        {
          id: "add-panel-none",
          label: "No widgets installed",
          disabled: true,
          action: () => {},
        },
      ];
  contextMenuStore.open(x, y, { kind: "desktop" }, items);
}

export function WidgetPanel() {
  const [, bump] = useState(0);
  useEffect(() => osStore.subscribe(() => bump((n) => n + 1)), []);

  if (!osStore.panelOpen) return null;

  const mods = osStore.widgetModules();
  const manifestFor = (moduleId: string) => mods.find((m) => m.id === moduleId);

  return (
    <>
      <div
        className="os-widget-panel__scrim"
        onClick={() => osStore.setPanelOpen(false)}
      />
      <aside className="os-widget-panel" role="complementary" aria-label="Widgets">
        <header className="os-widget-panel__head">
          <span className="os-widget-panel__title">Widgets</span>
          <button
            type="button"
            className="os-widget-panel__add"
            title="Add widget"
            onClick={(e: ReactMouseEvent) => openAddWidgetMenu(e.clientX, e.clientY)}
          >
            ＋ Add widget
          </button>
          <button
            type="button"
            className="os-widget-panel__close"
            title="Close"
            aria-label="Close widgets"
            onClick={() => osStore.setPanelOpen(false)}
          >
            ×
          </button>
        </header>

        <div className="os-widget-panel__list">
          {/* displayPanelItems(), not panelItems: a placement referencing a
              widget the server did not serve this account must not render. The
              stored entry is left alone so it comes back if the account that
              made it signs in again. */}
          {osStore.displayPanelItems().length === 0 && (
            <div className="os-widget-panel__empty">
              No widgets yet. Use “＋ Add widget”.
            </div>
          )}
          {osStore.displayPanelItems().map((p) => {
            const man = manifestFor(p.moduleId);
            const cols = man?.widget?.defaultCols ?? 2;
            const rows = man?.widget?.defaultRows ?? 2;
            const title = man?.widget?.title || man?.dock?.label || man?.name || p.moduleId;
            return (
              <div key={p.id} className="os-widget-panel__card">
                <div className="os-widget-panel__cardbar">
                  <span className="os-widget-panel__cardtitle">{title}</span>
                  <button
                    type="button"
                    className="os-widget-panel__cardbtn"
                    title="Open as app"
                    onClick={() => osStore.openApp(p.moduleId)}
                  >
                    Open as app
                  </button>
                  <button
                    type="button"
                    className="os-widget-panel__cardbtn is-remove"
                    title="Remove widget"
                    aria-label="Remove widget"
                    onClick={() => osStore.removePanelItem(p.id)}
                  >
                    ×
                  </button>
                </div>
                <div className="os-widget-panel__cardbody">
                  <WidgetHost
                    moduleId={p.moduleId}
                    placementId={p.id}
                    cols={cols}
                    rows={rows}
                    surface="panel"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </aside>
    </>
  );
}
